/* eslint-disable no-nested-ternary */
import * as yup from "yup";
import moment from "moment";
import { NomineeDetails, NomineeFields } from "../interfaces";
import regex from "../../../regex";
import { calcAgeFromToday } from "../resources";

const nomineeIndividualField: NomineeFields = {
  fullName: "",
  dob: "",
  email: "",
  captureAddress: false,
  captureBankDetails: false,
  bankName: "",
  bankAccountNumber: "",
  ifscCode: "",
  permanentAddress: "",
  permanentPincode: "",
  appointeePermanentAddress: "",
  appointeePermanentPincode: "",
  appointeeAddress: "",
  appointeePincode: "",
  appointeeMobileNo: "",
  appointeeBankAccountNumber: "",
  appointeeIfscCode: "",
  appointeeBankName: "",
  appointeeEmail: "",
  captureAppointeeBankDetails: false,
  captureAppointeeAddress: false,
  appointeeName: "",
  appointeeDob: "",
  appointeeRelation: "",
  gender: "",
  genderCode: "",
  relationship: "",
  share: "100",
  mobileNo: "",
  relationshipCode: "",
  appointeeRelationCode: "",
  isMinor: false,
  id: "",
  address: "",
  isAddressSame: false,
  pincode: "",
  appointeeGender: "",
  appointeeGenderCode: "",
  title: "",
  titleCode: "",
  appointeeTitle: "",
  appointeeTitleCode: "",
  isAppointeeAddressSameAsProposer: false
};

const initialValues: NomineeDetails = {
  nominee: [nomineeIndividualField]
};

const validationSchema = (nomineeDetailsSchema: any) =>
  yup.object().shape<Record<keyof NomineeDetails, yup.AnySchema>>({
    nominee: yup
      .array()
      .of(
        yup.object().shape<Record<keyof NomineeFields, yup.AnySchema>>({
          gender: nomineeDetailsSchema?.gender?.required
            ? yup.string().required("Gender is Required")
            : yup.string(),
          email: nomineeDetailsSchema?.email?.required
            ? yup.string().matches(regex.email, "Invalid Email").required("Email is Required")
            : yup.string().matches(regex.email, "Invalid Email"),
          captureAddress: yup.boolean(),
          captureBankDetails: yup.boolean(),
          bankName: yup
            .string()
            .when("captureBankDetails", ([captureBankDetails], schema) =>
              captureBankDetails && nomineeDetailsSchema?.bankName?.required
                ? schema.required("Bank Name is Required")
                : schema
            ),
          bankAccountNumber: yup
            .string()
            .when("captureBankDetails", ([captureBankDetails], schema) =>
              captureBankDetails && nomineeDetailsSchema?.bankAccountNumber?.required
                ? schema
                    .required("Account Number is Required")
                    .matches(regex.numeric, "Invalid Account Number")
                    .min(9, "Min 9 digits allowed")
                    .max(18, "Max 18 digits allowed")
                : schema
                    .matches(regex.numeric, "Invalid Account Number")
                    .min(9, "Min 9 digits allowed")
                    .max(18, "Max 18 digits allowed")
            ),
          ifscCode: yup.string().when("captureBankDetails", {
            is: (captureBankDetails: any) =>
              captureBankDetails && nomineeDetailsSchema?.ifscCode?.required,
            then: (schema) =>
              schema
                .required("IFSC Code is required")
                .test("ifsc-format", "Invalid IFSC format (e.g., SBIN0001234)", (value) => {
                  if (!value) return false;

                  const pattern = /^[A-Za-z]{4}[0-9]{7}$/;
                  return pattern.test(value);
                }),
            otherwise: (schema) => schema.notRequired()
          }),
          permanentAddress: yup
            .string()
            .when("captureAddress", ([captureAddress], schema) =>
              captureAddress && nomineeDetailsSchema?.permanentAddress?.required
                ? schema.required("Permanent Address is Required")
                : schema
            ),
          permanentPincode: yup
            .string()
            .when("captureAddress", ([captureAddress], schema) =>
              captureAddress && nomineeDetailsSchema?.permanentPincode?.required
                ? schema
                    .required("Pincode is Required")
                    .matches(regex.numeric, "Invalid Pincode")
                    .min(6, "Min 6 digits allowed")
                    .max(6, "Max 6 digits allowed")
                : schema
                    .matches(regex.numeric, "Invalid Pincode")
                    .min(6, "Min 6 digits allowed")
                    .max(6, "Max 6 digits allowed")
            ),
          fullName: nomineeDetailsSchema?.fullName?.required
            ? yup
                .string()
                .required("FullName is Required")
                .matches(regex.nomineefullNamewithDot, "Invalid Name")
                .min(1, "Min 1 chars allowed")
                .max(60, "Max 60 chars allowed")
            : yup
                .string()
                .matches(regex.nomineefullNamewithDot, "Invalid Name")
                .min(1, "Min 1 chars allowed")
                .max(60, "Max 60 chars allowed"),
          dob: nomineeDetailsSchema?.dob?.required
            ? yup
                .string()
                .required("DOB is Required")
                .test("length", "Invalid date", (value) => value.length === 10)
                .test("is-valid-date", "Invalid date", (value) =>
                  moment(value, "DD/MM/YYYY").isValid()
                )
                .test("is-valid-date", "Invalid date", (value) =>
                  moment(value, "DD/MM/YYYY").isSameOrBefore(new Date())
                )
                .test("is-valid-date", "Invalid date", (value) =>
                  moment(value, "DD/MM/YYYY").isSameOrAfter(moment("01/01/1900", "DD/MM/YYYY"))
                )
                .default("")
            : yup
                .string()
                .test("length", "Invalid date", (value) => !value || value.length === 10)
                .test(
                  "is-valid-date",
                  "Invalid date",
                  (value) => !value || moment(value, "DD/MM/YYYY").isValid()
                )
                .default(""),
          isAddressSame: yup.boolean(),
          captureAppointeeBankDetails: yup.boolean(),
          captureAppointeeAddress: yup.boolean(),
          appointeeName: yup
            .string()
            .when("dob", ([dob], schema) =>
              calcAgeFromToday(dob) < 18
                ? nomineeDetailsSchema?.appointee?.fullName?.required
                  ? schema
                      .required("Appointee Name is Required")
                      .matches(regex.nomineefullNamewithDot, "Invalid Appointee Name")
                      .min(1, "Min 1 chars allowed")
                      .max(60, "Max 60 chars allowed")
                  : schema
                      .matches(regex.nomineefullNamewithDot, "Invalid Appointee Name")
                      .min(1, "Min 1 chars allowed")
                      .max(60, "Max 60 chars allowed")
                : schema.notRequired()
            ),
          appointeeDob: yup.string().when("dob", ([dob], schema) =>
            calcAgeFromToday(dob) < 18
              ? nomineeDetailsSchema?.appointee?.dob?.required
                ? schema
                    .required("Appointee DOB is Required")
                    .test(
                      "is-adult",
                      "Appointee must be 18 years or older",
                      (value) => calcAgeFromToday(value) >= 18
                    )
                    .test("is-valid-date", "Invalid Appointee date", (value) =>
                      moment(value, "DD/MM/YYYY").isValid()
                    )
                : schema.test(
                    "is-adult",
                    "Appointee must be 18 years or older",
                    (value) => !value || calcAgeFromToday(value) >= 18
                  )
              : schema.notRequired()
          ),
          appointeeRelation: yup
            .string()
            .when("dob", ([dob], schema) =>
              calcAgeFromToday(dob) < 18
                ? nomineeDetailsSchema?.appointee?.relationshipWithPrimary?.required
                  ? schema.required("Appointee Relation is Required")
                  : schema
                : schema.notRequired()
            ),
          appointeeEmail: yup
            .string()
            .when("dob", ([dob], schema) =>
              calcAgeFromToday(dob) < 18
                ? nomineeDetailsSchema?.appointee?.email?.required
                  ? schema.required("Email is Required").matches(regex.email, "Invalid Email")
                  : schema.matches(regex.email, "Invalid Email")
                : schema.notRequired()
            ),
          appointeeMobileNo: yup
            .string()
            .when("dob", ([dob], schema) =>
              calcAgeFromToday(dob) < 18
                ? nomineeDetailsSchema?.appointee?.mobileNo?.required
                  ? schema
                      .required("Mobile No is Required")
                      .matches(regex.numeric, "Invalid Mobile")
                      .min(10, "Min 10 digits allowed")
                      .max(16, "Max 16 digits allowed")
                  : schema
                      .matches(regex.numeric, "Invalid Mobile")
                      .min(10, "Min 10 digits allowed")
                      .max(16, "Max 16 digits allowed")
                : schema.notRequired()
            ),
          appointeeBankName: yup
            .string()
            .when(["dob", "captureAppointeeBankDetails"], ([dob, capture], schema) =>
              calcAgeFromToday(dob) < 18 && capture
                ? nomineeDetailsSchema?.appointee?.appointeeBankName?.required
                  ? schema.required("Bank Name is Required")
                  : schema.notRequired()
                : schema.notRequired()
            ),
          appointeeBankAccountNumber: yup
            .string()
            .when(["dob", "captureAppointeeBankDetails"], ([dob, capture], schema) =>
              calcAgeFromToday(dob) < 18 && capture
                ? nomineeDetailsSchema?.appointee?.appointeeBankAccountNumber?.required
                  ? schema
                      .required("Account Number is Required")
                      .matches(regex.numeric, "Invalid Account Number")
                      .min(9, "Min 9 digits allowed")
                      .max(18, "Max 18 digits allowed")
                  : schema.notRequired()
                : schema.notRequired()
            ),
          appointeeIfscCode: yup.string().when(["dob", "captureAppointeeBankDetails"], {
            is: (dob: any, capture: any) => capture && calcAgeFromToday(dob) < 18,
            then: (schema) =>
              schema
                .required("IFSC Code is required")
                .test("ifsc-format", "Invalid IFSC format (e.g., SBIN0001234)", (value) => {
                  if (!value) return false;

                  const pattern = /^[A-Za-z]{4}[0-9]{7}$/;
                  return pattern.test(value);
                }),
            otherwise: (schema) => schema.notRequired()
          }),
          relationship: nomineeDetailsSchema?.relationshipWithPrimary?.required
            ? yup.string().required("Relationship is Required")
            : yup.string(),
          share: nomineeDetailsSchema?.sharePercentage?.required
            ? yup
                .string()
                .matches(regex.numeric, "Invalid Share Percentage")
                .required("% Share is Required")
                .test("len", "% Share should be between 1 and 100", (value) => {
                  const shareValue = Number(value);
                  return shareValue > 0 && shareValue <= 100;
                })
            : yup
                .string()
                .matches(regex.numeric, "Invalid Share Percentage")
                .test("len", "% Share should be between 1 and 100", (value) => {
                  if (!value) return true;
                  const shareValue = Number(value);
                  return shareValue > 0 && shareValue <= 100;
                }),
          mobileNo: nomineeDetailsSchema?.mobileNo?.required
            ? yup
                .string()
                .required("Mobile No is Required")
                .matches(regex.numeric, "Invalid Mobile")
                .min(10, "Min 10 digits allowed")
                .max(16, "Max 16 digits allowed")
            : yup
                .string()
                .matches(regex.numeric, "Invalid Mobile")
                .min(10, "Min 10 digits allowed")
                .max(16, "Max 16 digits allowed"),
          address: yup
            .string()
            .when(["captureAddress"], ([capture]) =>
              capture && nomineeDetailsSchema?.address?.required
                ? yup
                    .string()
                    .required("Address is Required")
                    .matches(regex.address1, "Invalid Address")
                    .min(2, "Min 2 values allowed")
                    .max(200, "Max 200 values allowed")
                : yup
                    .string()
                    .matches(regex.address1, "Invalid Address")
                    .min(2, "Min 2 values allowed")
                    .max(200, "Max 200 values allowed")
            ),
          pincode: yup
            .string()
            .when(["captureAddress"], ([capture]) =>
              capture && nomineeDetailsSchema?.pincode?.required
                ? yup
                    .string()
                    .required("Pincode is Required")
                    .matches(regex.pincode, "Invalid Pincode")
                : yup.string().matches(regex.pincode, "Invalid Pincode")
            ),
          appointeePincode: yup
            .string()
            .when(["dob", "captureAppointeeAddress"], ([dob, capture], schema) =>
              calcAgeFromToday(dob) < 18 && capture
                ? nomineeDetailsSchema?.appointee?.pincode?.required
                  ? schema
                      .required("Appointee Pincode is Required")
                      .matches(regex.pincode, "Invalid Appointee Pincode")
                  : schema.matches(regex.pincode, "Invalid Appointee Pincode")
                : schema.notRequired()
            ),
          appointeeAddress: yup
            .string()
            .when(["dob", "captureAppointeeAddress"], ([dob, capture], schema) =>
              calcAgeFromToday(dob) < 18 && capture
                ? nomineeDetailsSchema?.appointee?.address?.required
                  ? schema
                      .required("Appointee Address is Required")
                      .matches(regex.address1, "Invalid Appointee Address")
                      .min(1, "Min 1 values allowed")
                      .max(200, "Max 200 values allowed")
                  : schema
                      .matches(regex.address1, "Invalid Appointee Address")
                      .min(1, "Min 1 values allowed")
                      .max(200, "Max 200 values allowed")
                : schema.notRequired()
            ),
          appointeePermanentAddress: yup.string().notRequired(),
          appointeePermanentPincode: yup.string().notRequired(),
          appointeeGender: yup
            .string()
            .when("dob", ([dob], schema) =>
              calcAgeFromToday(dob) < 18
                ? nomineeDetailsSchema?.appointee?.gender?.required
                  ? schema.required("Appointee Gender is Required")
                  : schema
                : schema.notRequired()
            ),
          title: nomineeDetailsSchema?.title?.required
            ? yup.string().required("Title is Required")
            : yup.string(),
          appointeeTitle: yup
            .string()
            .when("dob", ([dob], schema) =>
              calcAgeFromToday(dob) < 18
                ? nomineeDetailsSchema?.appointee?.title?.required
                  ? schema.required("Appointee Title is Required")
                  : schema
                : schema.notRequired()
            ),
          genderCode: yup.string(),
          relationshipCode: yup.string(),
          appointeeRelationCode: yup.string(),
          isMinor: yup.boolean(),
          id: yup.string(),
          appointeeGenderCode: yup.string(),
          titleCode: yup.string(),
          appointeeTitleCode: yup.string(),
          isAppointeeAddressSameAsProposer: yup.boolean()
        })
      )
      .test("sum", "Sum of % Share for all Nominees must be 100", (nominee = []) => {
        if (nominee.length === 0) {
          return true;
        }
        const sumOfShares: number = nominee.reduce(
          (acc: number, val: any) => acc + Number(val?.share || 0),
          0
        );
        return sumOfShares === 100;
      })
  });

export { initialValues, nomineeIndividualField, validationSchema };
